/**
 * TODO(BMO-ADVISOR-INTEGRATION): SWAP POINT — application id minting.
 *
 * This helper is the client-only stand-in for what will become a
 * server-minted application id once real BMO Advisor Portal integration
 * lands. Every "Start new application" click in the pretend advisor flow
 * calls this to get a URL-scoped identifier; the id then threads through
 * the routes (`/advisor/workflows/:templateId/applications/:applicationId`)
 * and — when real draft persistence lands — into any storage key that
 * scopes state to a single application instance.
 *
 * When we integrate for real:
 *   • Replace the call site (advisor/AdvisorApplicationStartPage.tsx) with
 *     a `workflowsApi.startApplication(templateId)` that hits a new BE
 *     endpoint (`POST /api/v1/applications`) returning `{id}`.
 *   • Delete this file; every consumer already imports through it.
 *   • The URL shape doesn't have to change — the id just moves from
 *     "generated in the browser" to "issued by the server + persisted in
 *     DDB with the templateId + advisor oid + startedAt".
 *
 * Everything downstream of the URL param (route match, FormShell mount,
 * multi-tab isolation via distinct URLs) stays identical.
 */
export function mintApplicationId(): string {
  // Prefer `crypto.randomUUID` when available — the standards path. Modern
  // browsers only expose it in a secure context (HTTPS, localhost, file:).
  // Fall back to a `getRandomValues`-built UUIDv4 for the edge cases:
  //   • LAN-IP demos or corporate proxies stripping HTTPS
  //   • Older browsers (Chrome <92, Firefox <95, Safari <15.4)
  // Both paths use the same underlying CSPRNG (Web Crypto), so the fallback
  // is cryptographically equivalent, not a Math.random() downgrade.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return uuidV4Fallback();
}

function uuidV4Fallback(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  // Per RFC 4122 §4.4: set the version (0100 in the top nibble of byte 6)
  // and variant (10 in the top two bits of byte 8).
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}
